// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { forwardRef, type InputHTMLAttributes } from 'react'
import { cn } from '@/lib/utils'

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?:    string
  error?:    string
  hint?:     string
  required?: boolean
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, required, className, id, ...props }, ref) => {
    const inputId = id ?? label?.toLowerCase().replace(/\s+/g, '-')

    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <label
            htmlFor={inputId}
            className="text-sm font-medium text-ink-2"
          >
            {label}
            {required && (
              <span className="ml-1 text-sage-600" aria-hidden="true">*</span>
            )}
          </label>
        )}

        <input
          ref={ref}
          id={inputId}
          aria-describedby={
            error ? `${inputId}-error` :
            hint  ? `${inputId}-hint`  : undefined
          }
          aria-invalid={!!error}
          className={cn(
            'w-full rounded-xl border bg-white px-4 py-3',
            'text-sm text-ink placeholder:text-ink-4',
            'transition-colors duration-150',
            'focus:outline-none focus:ring-2 focus:ring-sage-500/20 focus:border-sage-500',
            error
              ? 'border-status-alertBorder focus:ring-red-400'
              : 'border-sand-300 hover:border-sage-300',
            className,
          )}
          {...props}
        />

        {error && (
          <p id={`${inputId}-error`} className="text-xs text-status-alertText" role="alert">
            {error}
          </p>
        )}
        {!error && hint && (
          <p id={`${inputId}-hint`} className="text-xs text-ink-4">
            {hint}
          </p>
        )}
      </div>
    )
  },
)

Input.displayName = 'Input'
